import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, XCircle, Tag, Check } from "lucide-react";
import { toast } from "sonner";

// Self-hosted Moyasar Payment Form v2.2.9 (served same-origin from /public/vendor)
// to avoid external-CDN load failures on production. Files under public/vendor/moyasar/.
const MOYASAR_JS_URL = `${process.env.PUBLIC_URL || ""}/vendor/moyasar/moyasar.umd.min.js`;
const MOYASAR_CSS_URL = `${process.env.PUBLIC_URL || ""}/vendor/moyasar/moyasar.css`;

// Stable error codes from the backend → localized messages.
const COUPON_ERRORS = {
  coupon_invalid: { ar: "كود الخصم غير صحيح أو غير مُفعّل.", en: "Invalid or inactive coupon." },
  coupon_expired: { ar: "انتهت صلاحية كود الخصم.", en: "This coupon has expired." },
  coupon_exhausted: { ar: "تم استنفاد عدد مرات استخدام هذا الكود.", en: "Coupon usage limit reached." },
  coupon_used_by_user: { ar: "لقد استخدمت هذا الكود من قبل.", en: "You already used this coupon." },
};

export default function PayPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  const [couponCode, setCouponCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [freeBusy, setFreeBusy] = useState(false);

  const T = (ar, en) => (lang === "ar" ? ar : en);
  const isFree = !!couponInfo && (couponInfo.free === true || Number(couponInfo.effective_sar) === 0);

  const checkoutFree = async () => {
    setFreeBusy(true);
    try {
      await api.post(`/orders/${orderId}/checkout-free`);
      toast.success(T("تم تفعيل الكوبون المجاني ووضع بحثك في الطابور 🎉", "Free coupon applied — your paper is queued 🎉"));
      navigate("/dashboard?tab=archive");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const mapped = COUPON_ERRORS[detail];
      toast.error(mapped ? mapped[lang === "ar" ? "ar" : "en"] : formatApiError(e));
    } finally {
      setFreeBusy(false);
    }
  };

  const fetchConfig = useCallback(async () => {
    const r = await api.post(`/payments/moyasar/orders/${orderId}/pay`);
    setConfig(r.data);
    return r.data;
  }, [orderId]);

  useEffect(() => {
    fetchConfig().catch((e) => setError(formatApiError(e)));
  }, [fetchConfig]);

  // Reflect a coupon already applied to this order (e.g. on the summary step)
  // so the pay page shows the discount consistently.
  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then(({ data }) => {
        if (data && data.coupon_code) {
          const disc = Number(data.coupon_discount_sar || 0);
          const eff = Number(data.effective_price_sar || 0);
          setCouponInfo({
            code: data.coupon_code,
            discount_sar: disc,
            effective_sar: eff,
            base_sar: Math.round((eff + disc) * 100) / 100,
            min_floor_applied: false,
            free: eff === 0,
          });
        }
      })
      .catch(() => {});
  }, [orderId]);

  // (Re)initialize the Moyasar form whenever the amount/config changes (e.g.
  // after a coupon is applied). The container is cleared first so re-init
  // replaces the form cleanly instead of stacking a second one.
  useEffect(() => {
    if (!config) return;
    if (isFree) return; // free coupon → no Moyasar form needed
    if (!document.querySelector(`link[href="${MOYASAR_CSS_URL}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = MOYASAR_CSS_URL;
      document.head.appendChild(link);
    }
    const initForm = () => {
      if (!window.Moyasar) return;
      const el = document.querySelector(".mysr-form");
      if (el) el.innerHTML = "";
      window.Moyasar.init({
        element: ".mysr-form",
        amount: config.amount,
        currency: config.currency,
        description: config.description,
        publishable_api_key: config.publishable_key,
        callback_url: config.callback_url,
        metadata: config.metadata,
        methods: ["creditcard", "applepay"],
        apple_pay: {
          country: "SA",
          // Moyasar 2.2.9 requires an ASCII-only label.
          label: "Zabatna",
          validate_merchant_url: "https://api.moyasar.com/v1/applepay/initiate",
        },
      });
    };
    if (window.Moyasar) {
      initForm();
    } else if (!document.querySelector(`script[src="${MOYASAR_JS_URL}"]`)) {
      const s = document.createElement("script");
      s.src = MOYASAR_JS_URL;
      s.async = true;
      s.onload = initForm;
      s.onerror = () => setError("Failed to load Moyasar.js");
      document.body.appendChild(s);
    }
  }, [config, isFree]);

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setApplying(true);
    setCouponError("");
    try {
      const { data } = await api.post(`/orders/${orderId}/apply-coupon`, { code });
      setCouponInfo(data);
      await fetchConfig(); // refresh amount → re-inits the Moyasar form
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const mapped = COUPON_ERRORS[detail];
      setCouponError(mapped ? mapped[lang === "ar" ? "ar" : "en"] : formatApiError(e));
      setCouponInfo(null);
    } finally {
      setApplying(false);
    }
  };

  const removeCoupon = async () => {
    setApplying(true);
    try {
      await api.post(`/orders/${orderId}/remove-coupon`);
      setCouponInfo(null);
      setCouponCode("");
      setCouponError("");
      await fetchConfig();
    } catch (e) {
      setCouponError(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  if (error) {
    return (
      <main className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-12" data-testid="pay-error-page">
        <Card className="w-full max-w-md p-8 border border-slate-200 shadow-sm text-center">
          <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">
            {T("تعذر فتح صفحة الدفع", "Couldn't open payment")}
          </h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            {T("العودة للوحة", "Back to dashboard")}
          </Button>
        </Card>
      </main>
    );
  }
  if (!config) {
    return (
      <main className="min-h-[calc(100vh-4rem)] grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-700" />
      </main>
    );
  }
  return (
    <main className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 py-6 sm:py-10" data-testid="pay-page">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-start">
          {T("إتمام الدفع", "Complete payment")}
        </h1>
        <p className="text-sm text-slate-500 text-start mb-6" data-testid="pay-summary">
          {config.description} — <b>{(config.amount / 100).toFixed(2)} SAR</b>
          <span className="ms-2 text-xs px-2 py-0.5 bg-slate-100 border border-slate-200 rounded">
            {config.mode}
          </span>
        </p>

        {/* Coupon */}
        <Card className="p-4 sm:p-5 border border-slate-200 shadow-none mb-4" data-testid="coupon-card">
          {!couponInfo ? (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                <Tag className="w-4 h-4 text-slate-500" />
                {T("هل لديك كود خصم؟", "Have a coupon?")}
              </label>
              <div className="flex gap-2">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                  placeholder={T("أدخل الكود", "Enter code")}
                  data-testid="coupon-input"
                  className="h-11 uppercase"
                  style={{ fontSize: "16px" }}
                />
                <Button
                  onClick={applyCoupon}
                  disabled={applying || !couponCode.trim()}
                  data-testid="coupon-apply-btn"
                  className="h-11 bg-slate-900 hover:bg-slate-800 shrink-0 px-5"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : T("تطبيق", "Apply")}
                </Button>
              </div>
              {couponError && (
                <p className="text-sm text-red-600 mt-2" data-testid="coupon-error">{couponError}</p>
              )}
            </div>
          ) : (
            <div data-testid="coupon-applied">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span className="font-bold text-emerald-700">{couponInfo.code}</span>
                  <span className="text-slate-500">
                    {T("خصم", "−")} {couponInfo.discount_sar.toFixed(2)} SAR
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeCoupon}
                  disabled={applying}
                  data-testid="coupon-remove-btn"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {T("إزالة", "Remove")}
                </Button>
              </div>
              <div className="mt-3 text-sm border-t border-slate-100 pt-3 space-y-1">
                <div className="flex justify-between text-slate-500">
                  <span>{T("السعر الأصلي", "Subtotal")}</span>
                  <span>{couponInfo.base_sar.toFixed(2)} SAR</span>
                </div>
                <div className="flex justify-between text-emerald-700">
                  <span>{T("الخصم", "Discount")}</span>
                  <span>−{couponInfo.discount_sar.toFixed(2)} SAR</span>
                </div>
                <div className="flex justify-between font-bold text-slate-900">
                  <span>{T("الإجمالي", "Total")}</span>
                  <span data-testid="coupon-effective-total">
                    {isFree ? T("مجاني بالكامل 🎉", "Completely free 🎉") : `${couponInfo.effective_sar.toFixed(2)} SAR`}
                  </span>
                </div>
              </div>
              {couponInfo.min_floor_applied && (
                <p className="text-[11px] text-amber-700 mt-2">
                  {T(
                    `الحد الأدنى للدفع ${couponInfo.min_payable_sar} ريال.`,
                    `Minimum payable is ${couponInfo.min_payable_sar} SAR.`
                  )}
                </p>
              )}
            </div>
          )}
        </Card>

        {isFree ? (
          <Card className="p-4 sm:p-6 border border-emerald-200 bg-emerald-50 shadow-none" data-testid="free-checkout-card">
            <div className="flex items-start gap-2 mb-4">
              <Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-sm text-emerald-900">
                {T(
                  "هذا الكوبون يجعل بحثك مجانياً بالكامل — لا حاجة للدفع. اضغط الزر أدناه لبدء التوليد.",
                  "This coupon makes your paper completely free — no payment needed. Click below to start generation."
                )}
              </p>
            </div>
            <Button
              onClick={checkoutFree}
              disabled={freeBusy}
              data-testid="free-checkout-btn"
              className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700"
            >
              {freeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : T("احصل على بحثك مجاناً 🎉", "Get your paper for free 🎉")}
            </Button>
          </Card>
        ) : (
          <Card className="p-4 sm:p-6 border border-slate-200 shadow-none">
            <div className="flex items-center gap-2 mb-4 text-xs text-slate-600 flex-wrap" data-testid="pay-methods-hint">
              <span className="px-2 py-1 bg-white border border-slate-200 rounded font-medium">
                {T("بطاقة ائتمانية", "Credit card")}
              </span>
              <span className="px-2 py-1 bg-white border border-slate-200 rounded font-medium">
                Apple Pay
              </span>
              <span className="text-slate-400">
                {T("— الدفع آمن ومشفّر", "— Secure encrypted payment")}
              </span>
            </div>
            <div className="mysr-form" data-testid="moyasar-form-container" />
          </Card>
        )}
      </div>
    </main>
  );
}
